import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Alert } from "../../components/Alert";
import { Modal } from "../../components/crm/Modal";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { Spinner } from "../../components/Spinner";
import { formatDate } from "../../lib/format";
import {
  createCategory,
  deleteCategory,
  isApiError,
  listCategories,
  supportErrorCode,
  updateCategory,
} from "../../lib/support-api";
import type { Category, CategoryInput } from "../../lib/support-types";
import type { TranslationKey } from "../../lib/i18n";
import { useConfig } from "../../state/config-context";

const ERROR_KEYS: Record<string, TranslationKey> = {
  invalid_reference: "support.error.invalidTransition",
};

interface FormState {
  id: string | null;
  name: string;
  slug: string;
  description: string;
  color: string;
  sortOrder: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  id: null,
  name: "",
  slug: "",
  description: "",
  color: "",
  sortOrder: "",
  isActive: true,
};

function SupportCategoriesInner(): ReactNode {
  const { t, lang } = useConfig();
  const [items, setItems] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const locale = lang === "es" ? "es-ES" : "en-US";

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const page = await listCategories();
      setItems(page.items);
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (form === null) {
      return;
    }
    setSaving(true);
    setError(null);
    const parsedOrder = Number.parseInt(form.sortOrder, 10);
    const input: CategoryInput = {
      name: form.name.trim(),
      slug: form.slug.trim() === "" ? undefined : form.slug.trim(),
      description: form.description.trim() === "" ? null : form.description.trim(),
      color: form.color.trim() === "" ? null : form.color.trim(),
      sort_order: Number.isNaN(parsedOrder) ? undefined : parsedOrder,
      is_active: form.isActive,
    };
    try {
      if (form.id === null) {
        await createCategory(input);
      } else {
        await updateCategory(form.id, input);
      }
      setForm(null);
      await load();
    } catch (caught) {
      const code = supportErrorCode(caught);
      const key = code === null ? undefined : ERROR_KEYS[code];
      setError(
        key !== undefined ? t(key) : isApiError(caught) ? caught.detail : t("error.network"),
      );
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await deleteCategory(id);
      setConfirmId(null);
      await load();
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="page" aria-labelledby="support-categories-title">
      <header className="page__header page__header--row">
        <div>
          <h1 id="support-categories-title">{t("support.categories.title")}</h1>
          <p className="muted">{t("support.categories.subtitle")}</p>
        </div>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => setForm({ ...EMPTY_FORM })}
        >
          {t("support.categories.new")}
        </button>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && items.length === 0 ? (
        <p className="empty">{t("support.categories.empty")}</p>
      ) : null}

      {!loading && items.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("support.categories.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("support.categories.name")}</th>
                <th scope="col">{t("support.categories.slug")}</th>
                <th scope="col">{t("support.categories.sortOrder")}</th>
                <th scope="col">{t("support.categories.isActive")}</th>
                <th scope="col">{t("support.ticket.updated")}</th>
                <th scope="col" className="table__actions-col">
                  <span className="sr-only">{t("support.common.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((category) => (
                <tr key={category.id}>
                  <td>{category.name}</td>
                  <td>
                    <code>{category.slug}</code>
                  </td>
                  <td>{String(category.sort_order)}</td>
                  <td>
                    {category.is_active
                      ? t("support.categories.isActive")
                      : t("support.common.none")}
                  </td>
                  <td>{formatDate(category.updated_at, locale)}</td>
                  <td className="table__actions-col">
                    <div className="inline-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() =>
                          setForm({
                            id: category.id,
                            name: category.name,
                            slug: category.slug,
                            description: category.description ?? "",
                            color: category.color ?? "",
                            sortOrder: String(category.sort_order),
                            isActive: category.is_active,
                          })
                        }
                      >
                        {t("common.edit")}
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setConfirmId(category.id)}
                      >
                        {t("support.common.delete")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {form !== null ? (
        <Modal
          title={form.id === null ? t("support.categories.create") : t("support.categories.edit")}
          onClose={() => setForm(null)}
          busy={saving}
        >
          <form onSubmit={(event) => void onSubmit(event)}>
            <div className="form-grid">
              <label className="field" htmlFor="category-name">
                <span>{t("support.categories.name")}</span>
                <input
                  id="category-name"
                  value={form.name}
                  maxLength={120}
                  required
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="category-slug">
                <span>{t("support.categories.slug")}</span>
                <input
                  id="category-slug"
                  value={form.slug}
                  maxLength={60}
                  aria-describedby="category-slug-hint"
                  onChange={(event) => setForm({ ...form, slug: event.target.value })}
                />
                <small id="category-slug-hint" className="field__hint">
                  {t("support.categories.slugHint")}
                </small>
              </label>
              <label className="field" htmlFor="category-description">
                <span>{t("support.categories.description")}</span>
                <textarea
                  id="category-description"
                  value={form.description}
                  rows={3}
                  maxLength={2000}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="category-color">
                <span>{t("support.categories.color")}</span>
                <input
                  id="category-color"
                  value={form.color}
                  placeholder="#2563eb"
                  aria-describedby="category-color-hint"
                  onChange={(event) => setForm({ ...form, color: event.target.value })}
                />
                <small id="category-color-hint" className="field__hint">
                  {t("support.categories.colorHint")}
                </small>
              </label>
              <label className="field" htmlFor="category-order">
                <span>{t("support.categories.sortOrder")}</span>
                <input
                  id="category-order"
                  inputMode="numeric"
                  value={form.sortOrder}
                  onChange={(event) => setForm({ ...form, sortOrder: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="category-active">
                <span>{t("support.categories.isActive")}</span>
                <input
                  id="category-active"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                />
              </label>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? t("common.saving") : t("common.save")}
              </button>
              <button type="button" className="btn btn--ghost" onClick={() => setForm(null)}>
                {t("common.cancel")}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {confirmId !== null ? (
        <Modal
          title={t("support.categories.deleteConfirm")}
          onClose={() => setConfirmId(null)}
          busy={saving}
          footer={
            <>
              <button
                type="button"
                className="btn btn--danger"
                disabled={saving}
                onClick={() => void onDelete(confirmId)}
              >
                {t("support.common.delete")}
              </button>
              <button type="button" className="btn btn--ghost" onClick={() => setConfirmId(null)}>
                {t("common.cancel")}
              </button>
            </>
          }
        >
          <p>{t("support.categories.deleteConfirm")}</p>
        </Modal>
      ) : null}
    </section>
  );
}

export function SupportCategoriesPage(): ReactNode {
  return (
    <RequirePermission permission="support.category.manage">
      <SupportCategoriesInner />
    </RequirePermission>
  );
}